import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useBooking } from '../../../contexts/BookingContext';

const PathwayCards = () => {
  const [expandedCard, setExpandedCard] = useState(null);
  const { openBooking } = useBooking();

  const pathways = [
    {
      id: 'executive',
      title: 'Executive Briefing Mode',
      subtitle: 'For C-Suite Decision Makers',
      description: 'Strategic overview with executive-level insights, market positioning analysis, and immediate ROI projections tailored for senior leadership.',
      icon: 'Crown',
      color: 'primary',
      features: [
        'Strategic Market Overview',
        'Executive ROI Dashboard',
        'Competitive Intelligence',
        'Risk Assessment Matrix'
      ],
      cta: 'Access Executive Intelligence',
      route: '/market-intelligence-hub-real-time-insights',
      badge: 'Most Popular',
      detailedContent: {
        overview: 'Our Executive Briefing Mode provides C-suite executives with comprehensive strategic intelligence for Global Capability Center decisions. This high-level overview cuts through complexity to deliver actionable insights that drive strategic decisions.',
        keyBenefits: [
          'Strategic Market Intelligence: Real-time analysis of GCC opportunities across key markets including India, Philippines, Poland, and emerging locations',
          'Executive ROI Dashboard: Sophisticated financial modeling showing potential cost savings, timeline projections, and risk-adjusted returns',
          'Competitive Intelligence: Analysis of how industry leaders are leveraging GCCs for competitive advantage',
          'Risk Assessment Matrix: Comprehensive evaluation of regulatory, operational, and market risks with mitigation strategies'
        ],
        businessImpact: {
          costSavings: '35-65% operational cost reduction',
          timeToMarket: '40% faster product development cycles',
          talentAccess: 'Access to 4.3M+ skilled professionals',
          scalability: 'Rapid scaling capabilities up to 1000+ resources'
        },
        nextSteps: [
          'Schedule executive briefing session',
          'Review market intelligence dashboard',
          'Assess strategic alignment with business objectives',
          'Develop preliminary implementation roadmap'
        ]
      }
    },
    {
      id: 'explorer',
      title: 'Market Explorer',
      subtitle: 'For Research-Driven Users',
      description: 'Comprehensive market intelligence with interactive data visualization, regulatory updates, and detailed location analysis.',
      icon: 'Search',
      color: 'secondary',
      features: [
        'Interactive Market Data',
        'Regulatory Compliance Guide',
        'Location Comparison Tool',
        'Talent Availability Metrics'
      ],
      cta: 'Explore Intelligence Hub',
      route: '/market-intelligence-hub-real-time-insights',
      badge: null,
      detailedContent: {
        overview: 'The Market Explorer provides research teams and analysts with deep-dive market intelligence, regulatory insights, and location-specific data to support informed GCC establishment decisions.',
        keyBenefits: [
          'Interactive Market Data: Real-time analytics on 15+ GCC-friendly locations with detailed cost comparisons, talent availability, and infrastructure ratings',
          'Regulatory Compliance Guide: Comprehensive regulatory frameworks, compliance requirements, and policy updates across key markets',
          'Location Comparison Tool: Side-by-side analysis of cities including cost structures, talent pools, infrastructure, and business environment',
          'Talent Availability Metrics: Detailed analysis of skill availability, compensation benchmarks, and attrition rates by function and seniority'
        ],
        businessImpact: {
          researchEfficiency: '75% reduction in market research time',
          decisionAccuracy: '90% improved location selection accuracy',
          riskMitigation: '60% reduction in regulatory compliance risks',
          costOptimization: 'Optimal cost structure identification'
        },
        nextSteps: [
          'Access interactive market dashboard',
          'Download regulatory compliance guides',
          'Run location comparison analysis',
          'Review talent availability reports'
        ]
      }
    },
    {
      id: 'roi',
      title: 'ROI Preview',
      subtitle: 'For Procurement Teams',
      description: 'Detailed cost-benefit analysis with customizable parameters, timeline projections, and comprehensive financial modeling.',
      icon: 'Calculator',
      color: 'accent',
      features: [
        'Cost-Benefit Calculator',
        'Timeline Projections',
        'Financial Modeling',
        'Savings Optimization'
      ],
      cta: 'Access ROI Tools',
      route: '/roi-calculator-hub-interactive-decision-tools',
      badge: 'New Tool',
      detailedContent: {
        overview: 'Our ROI Preview tools provide procurement and finance teams with sophisticated financial modeling capabilities to quantify GCC investment returns and optimize cost structures.',
        keyBenefits: [
          'Cost-Benefit Calculator: Advanced financial modeling with customizable parameters for different GCC models and scenarios',
          'Timeline Projections: Detailed implementation timelines with milestone-based cost and benefit realization schedules',
          'Financial Modeling: Comprehensive 5-year financial projections including setup costs, operational expenses, and ROI trajectories',
          'Savings Optimization: Analysis of cost optimization opportunities across different functions, locations, and operational models'
        ],
        businessImpact: {
          costSavings: '$2-15M annual savings potential',
          paybackPeriod: '12-18 months typical payback',
          productivity: '25-40% productivity improvements',
          scalability: 'Linear cost scaling with volume growth'
        },
        nextSteps: [
          'Run ROI calculation scenarios',
          'Download financial modeling templates',
          'Review cost optimization opportunities',
          'Schedule procurement consultation'
        ]
      }
    }
  ];

  const getCardStyles = (color) => {
    switch (color) {
      case 'primary':
        return 'border-primary/20';
      case 'secondary':
        return 'border-secondary/20';
      case 'accent':
        return 'border-accent/20';
      default:
        return 'border-border';
    }
  };

  const getIconColor = (color) => {
    switch (color) {
      case 'primary':
        return 'var(--color-primary)';
      case 'secondary':
        return 'var(--color-secondary)';
      case 'accent':
        return 'var(--color-accent)';
      default:
        return 'var(--color-text-primary)';
    }
  };

  const getBadgeStyles = (color) => {
    switch (color) {
      case 'primary':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'secondary':
        return 'bg-secondary/10 text-secondary border-secondary/20';
      case 'accent':
        return 'bg-accent/10 text-accent border-accent/20';
      default:
        return 'bg-muted text-text-secondary border-border';
    }
  };

  const handleCardClick = (pathwayId) => {
    setExpandedCard(expandedCard === pathwayId ? null : pathwayId);
  };

  return (
    <section className="py-16 bg-background-alt">
      <div className="container-executive">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-3 py-1 rounded-full text-xs font-body-medium mb-4">
            <Icon name="Route" size={14} />
            <span>Choose Your Journey</span>
          </div>
          
          <h2 className="font-headline font-headline-bold text-2xl lg:text-3xl text-text-primary mb-4">
            Three Pathways to
            <span className="block text-accent font-accent">GCC Success</span>
          </h2>
          
          <p className="text-sm text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Whether you're a strategic decision-maker, research analyst, or procurement professional, 
            we've designed specialized entry points that align with your specific needs and objectives.
          </p>
        </div>

        {/* Pathway Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {pathways.map((pathway) => (
            <div
              key={pathway.id}
              className={`relative bg-card border-2 rounded-xl p-6 pt-8 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-xl ${getCardStyles(pathway.color)} ${
                expandedCard === pathway.id ? 'ring-2 ring-accent/20' : ''
              }`}
              onClick={() => handleCardClick(pathway.id)}
            >
              {/* Badge */}
              {pathway.badge && (
                <div className={`absolute top-2 left-4 px-2 py-1 rounded-full text-xs font-body-medium border ${getBadgeStyles(pathway.color)}`}>
                  {pathway.badge}
                </div>
              )}

              {/* Icon */}
              <div className="mb-4">
                <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                  <Icon 
                    name={pathway.icon} 
                    size={24} 
                    color={getIconColor(pathway.color)}
                    strokeWidth={1.5}
                  />
                </div>
              </div>

              {/* Content */}
              <div className="space-y-3 mb-6">
                <div>
                  <h3 className="font-headline font-headline-bold text-lg text-text-primary mb-1">
                    {pathway.title}
                  </h3>
                  <p className="text-xs text-accent font-body-medium">
                    {pathway.subtitle}
                  </p>
                </div>

                <p className="text-text-secondary leading-relaxed text-sm">
                  {pathway.description}
                </p>

                {/* Features List */}
                <div className="space-y-1">
                  {pathway.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="w-1 h-1 bg-accent rounded-full flex-shrink-0"></div>
                      <span className="text-xs text-text-secondary">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Click indicator */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-accent font-body-medium">
                  {expandedCard === pathway.id ? 'Click to collapse' : 'Click for details'}
                </span>
                <Icon 
                  name={expandedCard === pathway.id ? 'ChevronUp' : 'ChevronDown'} 
                  size={16} 
                  color="var(--color-accent)"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Expanded Content */}
        {expandedCard && (
          <div className="mb-8">
            {pathways.filter(p => p.id === expandedCard).map((pathway) => (
              <div key={pathway.id} className="bg-card border border-border rounded-2xl p-8 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                      <Icon 
                        name={pathway.icon} 
                        size={20} 
                        color={getIconColor(pathway.color)}
                      />
                    </div>
                    <div>
                      <h3 className="font-headline font-headline-bold text-xl text-text-primary">
                        {pathway.title} - Detailed Overview
                      </h3>
                      <p className="text-sm text-accent">{pathway.subtitle}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setExpandedCard(null)}
                    className="text-text-secondary hover:text-primary transition-colors"
                  >
                    <Icon name="X" size={20} />
                  </button>
                </div>

                <div className="grid lg:grid-cols-2 gap-8">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Overview */}
                    <div>
                      <h4 className="font-headline font-headline-bold text-lg text-text-primary mb-3">
                        Overview
                      </h4>
                      <p className="text-text-secondary leading-relaxed">
                        {pathway.detailedContent.overview}
                      </p>
                    </div>

                    {/* Key Benefits */}
                    <div>
                      <h4 className="font-headline font-headline-bold text-lg text-text-primary mb-3">
                        Key Benefits
                      </h4>
                      <div className="space-y-3">
                        {pathway.detailedContent.keyBenefits.map((benefit, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0"></div>
                            <p className="text-text-secondary text-sm leading-relaxed">
                              <strong className="text-primary">{benefit.split(':')[0]}:</strong>
                              {benefit.split(':').slice(1).join(':')}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Business Impact */}
                    <div>
                      <h4 className="font-headline font-headline-bold text-lg text-text-primary mb-3">
                        Business Impact
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        {Object.entries(pathway.detailedContent.businessImpact).map(([key, value]) => (
                          <div key={key} className="bg-background border border-border rounded-lg p-4">
                            <div className="font-body font-body-medium text-accent text-sm capitalize mb-1">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </div>
                            <div className="font-headline font-headline-bold text-text-primary text-lg">
                              {value}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Next Steps */}
                    <div>
                      <h4 className="font-headline font-headline-bold text-lg text-text-primary mb-3">
                        Next Steps
                      </h4>
                      <div className="space-y-2">
                        {pathway.detailedContent.nextSteps.map((step, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <div className="w-6 h-6 bg-accent/10 text-accent rounded-full flex items-center justify-center text-xs font-body-medium">
                              {index + 1}
                            </div>
                            <span className="text-text-secondary text-sm">{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="pt-4 border-t border-border">
                      <Button
                        variant="default"
                        size="default"
                        className="bg-cta text-cta-foreground w-full"
                        iconName="ArrowRight"
                        iconPosition="right"
                        iconSize={16}
                        asChild
                      >
                        <Link to={pathway.route}>
                          {pathway.cta}
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="text-center">
          <div className="bg-card border border-border rounded-2xl p-6 max-w-2xl mx-auto shadow-lg">
            <h3 className="font-headline font-headline-bold text-lg text-text-primary mb-3">
              Need Personalized Guidance?
            </h3>
            <p className="text-text-secondary mb-4 text-sm">
              Our strategic advisors are available for executive consultations to help you choose the optimal pathway for your organization.
            </p>
            <Button
              variant="primary"
              size="default"
              className="bg-cta text-cta-foreground"
              iconName="Calendar"
              iconPosition="left"
              iconSize={16}
              onClick={() => openBooking('pathway_cards')}
            >
              Schedule Executive Consultation
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PathwayCards;